// #357 change #2 — run-out forecast: "at this pace your credits last until <date>".
// Pure so it can be unit-tested. Pace = credits used over the last up-to-14 COMPLETED days, divided by
// the days since first usage in that window (capped at 14); today is excluded as a partial day.
import type { UsageDataPoint } from '@common-reducers/backend-api/backend-api';

export const FORECAST_WINDOW_DAYS = 14;
/** Fewer active days than this is noise (one burst), so we show no forecast. */
export const FORECAST_MIN_ACTIVE_DAYS = 3;

// Days covered by each selector value (parseInt would read '12months' as 12 days).
const TIMEFRAME_DAYS: Record<string, number> = { '7days': 7, '30days': 30, '90days': 90, '12months': 365 };
export const timeframeDays = (v: string): number => TIMEFRAME_DAYS[v] ?? 30;

export type CreditsForecast =
  | { kind: 'runs_out'; runOutDate: Date; daysLeft: number }
  | { kind: 'renews_first' };

/** Backend sends the chart label as DD/MM/YYYY. Returns local midnight, or null if malformed. */
export function parseUsageDay(label: string): Date | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(label ?? '');
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Workspace credits used per day over the last up-to-14 completed days, or null when there is not
 * enough signal. `usageData` is the workspace-aggregated series (the backend sums by workspaceId), so
 * the pace is the whole team's, not the viewer's own.
 */
export function computeDailyPace(args: {
  usageData: Array<Pick<UsageDataPoint, 'usage'> & { name?: string; date?: string }>;
  loadedDays?: number;
  now?: Date;
}): number | null {
  const { usageData, loadedDays = 30, now = new Date() } = args;
  const windowDays = Math.max(1, Math.min(FORECAST_WINDOW_DAYS, loadedDays));
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayAt = (offset: number) => new Date(today.getFullYear(), today.getMonth(), today.getDate() + offset).getTime();
  // Completed days only: today is partial and would understate the pace.
  const windowStart = dayAt(-windowDays);
  const windowEnd = dayAt(-1);

  let used = 0;
  let activeDays = 0;
  let earliest = Infinity;
  for (const p of usageData ?? []) {
    const day = parseUsageDay((p.name ?? p.date) as string);
    if (!day || day.getTime() < windowStart || day.getTime() > windowEnd) continue;
    if (p.usage > 0 && Number.isFinite(p.usage)) {
      used += p.usage;
      activeDays++;
      earliest = Math.min(earliest, day.getTime());
    }
  }
  if (activeDays < FORECAST_MIN_ACTIVE_DAYS || used <= 0) return null;

  // History-aware denominator: a young account (first usage 3 days ago) must not be divided by 14.
  // Days with no usage inside the span count as zero (the backend only returns days with usage).
  const spanDays = Math.round((windowEnd - earliest) / 86_400_000) + 1;
  const denom = Math.max(1, Math.min(windowDays, spanDays));
  return used / denom;
}

export function computeCreditsForecast(args: {
  usageData: Array<Pick<UsageDataPoint, 'usage'> & { name?: string; date?: string }>;
  creditsRemaining: number;
  /** Days until the credits renew (from the subscription). */
  daysToRenewal: number;
  /** How many days the loaded usage covers (7/30/90 selector); the pace window is min(14, this). */
  loadedDays?: number;
  now?: Date;
}): CreditsForecast | null {
  const { usageData, creditsRemaining, daysToRenewal, loadedDays = 30, now = new Date() } = args;
  if (!(creditsRemaining > 0)) return null; // out of credits: the warning/empty state owns that
  const perDay = computeDailyPace({ usageData, loadedDays, now });
  if (perDay === null) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const daysLeft = creditsRemaining / perDay;
  if (daysToRenewal > 0 && daysLeft >= daysToRenewal) return { kind: 'renews_first' };
  const wholeDays = Math.max(0, Math.floor(daysLeft));
  return { kind: 'runs_out', daysLeft: wholeDays, runOutDate: new Date(today.getFullYear(), today.getMonth(), today.getDate() + wholeDays) }; // calendar math: DST-safe
}
